import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-gray-500 text-xxs2 italic"
}

import { useGetInspectionGroupQuery } from '@/entities/inspection-group/hooks';
import { computed, inject, reactive, Ref, ref, watch } from 'vue';
import { required } from '@vuelidate/validators';

import { useCreateAnnotationMutation } from '@/entities/annotation/hooks';
import { useForm } from '@/core/hooks/forms';

import { Annotation } from '@/entities/annotation/types';
import { AnnotationSeverity } from '@/entities/annotation-severity/types';
import { AnnotationType } from '@/entities/annotation-type/types';
import {
  InspectionViewerCreateAnnotationFormInjection,
  inspectionViewerCreateAnnotationFormInjectionKey,
  InspectionViewerInjection,
  inspectionViewerInjectionKey,
} from '@/teams/visionair/components/InspectionViewer/inspection-viewer.shared';
import { OrganizationInjection, OrganizationInjectionKey } from '@/teams/visionair/components/keys';

import {
  NkButton,
  NkForm,
  NkFormItem,
  NkGroup,
  NkOption,
  NkSelect,
  NkTextArea
} from '@/core/components';
import AnnotationSeveritySelect
  from '@/entities/annotation-severity/components/inputs/AnnotationSeveritySelect.vue';
import { ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { orderBy } from 'lodash';
import { useGetAnnotationComponentsQuery } from '@/entities/annotation-component/hooks';
import { useGetAnnotationTypesQuery } from '@/entities/annotation-type/hooks';
import {
  useGetAnnotationTypeOrganizationsQuery
} from '@/entities/annotation-type-organization/hooks';
import { AnnotationComponent } from '@/entities/annotation-component/types';
import { retrieveZone } from '@/entities/device-zone/services/determineZone';
import { useGetAnnotationStandardsQuery } from '@/entities/annotation-standard/hooks';
import { useGetDeviceQuery } from '@/entities/device/hooks';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionViewerCreateAnnotationFormSherlock',
  setup(__props) {

const { t, locale } = useI18n();

const injected = inject(inspectionViewerCreateAnnotationFormInjectionKey) as InspectionViewerCreateAnnotationFormInjection;
const { props } = injected;

const { organizationId } = inject(OrganizationInjectionKey) as OrganizationInjection;
const { imageHeight } = inject(inspectionViewerInjectionKey) as InspectionViewerInjection;

const zone = computed(() => retrieveZone({
  y: props.completeLocation.pixelLocation.y,
  height: props.completeLocation.pixelLocation.height
}, imageHeight.value.y))

const createAnnotationForm = ref();
// Form
const fields: { severity: AnnotationSeverity['id'] | null, type: AnnotationType['id'] | null, comment: Annotation['comment'], component: AnnotationComponent['id'] | null } = reactive({
  severity: null,
  type: null,
  comment: '',
  component: null
});

const rules = {
  component: { required },
  severity: { required },
  type: { required },
  comment: { required }
};

const inspectionGroupQueryData = computed(() => ({ id: props.inspectionGroup }));
const hasInspectionGroupQueryData = computed(() => !!inspectionGroupQueryData.value.id);
const { data: inspectionGroupData } = useGetInspectionGroupQuery(inspectionGroupQueryData, { enabled: hasInspectionGroupQueryData });

const deviceQueryData = computed(() => ({ id: props.device }));
const hasDeviceQueryData = computed(() => !!deviceQueryData.value.id);
const { data: device } = useGetDeviceQuery(deviceQueryData, { enabled: hasDeviceQueryData });
const isBladeDevice = computed(() => device.value?.type && ![14,15,16,17,18,19].includes(device.value.type));

/**
 * Retrieve all annotationComponents based on the organization.
 */
const annotationComponentsQueryFilters: Ref = computed(() => ({ organizations: organizationId.value, itemsPerPage: 50, forBlade: isBladeDevice.value }));
const hasOrganizationId = computed(() => !!organizationId.value)
const { data: annotationComponentData, isLoading: loadingAnnotationComponent } = useGetAnnotationComponentsQuery({
  refetchOnWindowFocus: false,
  enabled: hasOrganizationId
}, annotationComponentsQueryFilters);

const annotationComponents = computed(() => orderBy((annotationComponentData.value || []), ['name']));

/**
 * Retrieve all annotationType ids based on the component selected by the user and the organization
 */
const typeWithComponentQueryFilters: Ref = computed(() => ({ 'organization.id': organizationId.value, 'component.id': fields.component}));
const hasComponentSelected = computed(() => (fields.component !== null))
const { data: annotationTypeOrganization } = useGetAnnotationTypeOrganizationsQuery({
  refetchOnWindowFocus: false,
  enabled: hasComponentSelected
}, typeWithComponentQueryFilters)

/**
 * Retrieve all annotations types information based of the precedent query
 */
const annotationTypesIds = computed(() => (annotationTypeOrganization.value?.map(type => type.type)));
const annotationTypesQueryFilters: Ref = computed(() => ({ ids: annotationTypesIds.value, itemsPerPage: 50 }));
const hasFetchAnnotationTypesIds = computed(() => !!annotationTypesIds.value?.length);
const { data: annotationTypesData } = useGetAnnotationTypesQuery({
  refetchOnWindowFocus: false,
  enabled: hasFetchAnnotationTypesIds
}, annotationTypesQueryFilters);
const annotationTypes = computed(() => orderBy((annotationTypesData.value || []), ['order', 'name']));

/**
 * Retrieve the annotation standard based on annotationType selected and the zone
 */
const annotationStandardQueryFilters: Ref = computed(() => {
  const filters : {'type.id': number | null, 'zone'?: string | null} = { 'type.id': fields.type }

  if (isBladeDevice.value) {
    filters.zone = zone.value.name
  }

  return filters
});
const hasSelectedAnnotationType = computed(() => !!fields.type)
const { data: annotationStandardsData } = useGetAnnotationStandardsQuery({
  refetchOnWindowFocus: false,
  enabled: hasSelectedAnnotationType
}, annotationStandardQueryFilters)
const annotationStandard = computed(() => (annotationStandardsData.value?.find(Boolean)))

/**
 * Update the comment and the severity when the annotationType is changed
 */
watch(annotationStandardsData, () => {
  const standard = annotationStandard.value ;
  if ( standard ) {
    fields.comment = standard.comment.en
    fields.severity = standard.severity
  } else {
    fields.comment = ''
    fields.severity = null
  }
});

watch (
  () => fields.severity,
  (newValue, oldValue) => {
    const standard = annotationStandardsData.value?.find(annot => annot.severity === newValue);
    if(standard) {
      fields.comment = standard.comment.en
    }
  }
)

watch (
  () => fields.component,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      fields.type = null;
    }
  }
)

const { onSubmitted } = useForm(injected.emit);

const { mutateAsync: createAnnotationMutation } = useCreateAnnotationMutation();

const createAnnotation = () => {
  createAnnotationForm.value.validator.$validate()
    .then((valid: boolean) => {
      if (valid) {
        const currentLocale = locale.value ?? 'en'

        const expertComment = fields.comment === annotationStandard.value?.comment[currentLocale]
          ? annotationStandard.value?.comment
          : { en: fields.comment, fr: fields.comment }

        createAnnotationMutation({
          position: {
            pixelX: Math.round(props.completeLocation.pixelLocation.x),
            pixelY: Math.round(props.completeLocation.pixelLocation.y),
            pixelHeight: Math.round(props.completeLocation.pixelLocation.height),
            pixelWidth: Math.round(props.completeLocation.pixelLocation.width),
            viewportX: props.completeLocation.viewportLocation.x,
            viewportY: props.completeLocation.viewportLocation.y,
            viewportHeight: props.completeLocation.viewportLocation.height,
            viewportWidth: props.completeLocation.viewportLocation.width,
            hubHeight: 0,
            position: 0,
            device: props.device,
          },
          annotationGroups: [],
          annotationImages: [],
          inspection: props.inspection,
          severity: fields.severity as AnnotationSeverity['id'],
          type: fields.type as AnnotationType['id'],
          comment: null,
          expertComment,
          createdBy: null,
          status: 'to fix'
        },
          {
            onSuccess: () => {
              ElNotification({
                title: t('annotation.notification.add.success.title'),
                message: t('annotation.notification.add.success.message'),
                type: 'success',
                position: 'bottom-right',
              });
            }
          }).then(() => {
          onSubmitted();
        }).catch(() => {
          console.log('ANNOTATION CREATE ERROR');
        });
      }
    });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(NkForm), {
    ref_key: "createAnnotationForm",
    ref: createAnnotationForm,
    fields: fields,
    rules: rules
  }, {
    footer: _withCtx(() => [
      _createVNode(_unref(NkGroup), { position: "right" }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkButton), { onClick: createAnnotation }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('annotation.action.add')), 1)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(NkFormItem), {
        prop: "component",
        label: _ctx.$t('misc.component')
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkSelect), {
            modelValue: fields.component,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fields.component) = $event)),
            isDisabled: _unref(loadingAnnotationComponent)
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(annotationComponents), (component) => {
                return (_openBlock(), _createBlock(_unref(NkOption), {
                  key: component.id,
                  value: component.id,
                  label: component.name[_unref(locale)]
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(component.name[_unref(locale)]), 1)
                  ]),
                  _: 2
                }, 1032, ["value", "label"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue", "isDisabled"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.type'),
        prop: "type"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkSelect), {
            modelValue: fields.type,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fields.type) = $event)),
            disabled: fields.component === null,
            isDisabled: fields.component === null
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(annotationTypes), (type) => {
                return (_openBlock(), _createBlock(_unref(NkOption), {
                  key: type.id,
                  value: type.id,
                  label: type.name
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(type.name) + " ", 1),
                    ((_ctx.$store.getters['connection/isGranted'](['ROLE_ADMIN']) || _ctx.$store.getters['connection/isGranted'](['annotation_standard_view'])) && type.help[_unref(locale)] !== null)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_1, " // " + _toDisplayString(type.help[_unref(locale)]), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["value", "label"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue", "disabled", "isDisabled"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.severity'),
        prop: "severity"
      }, {
        default: _withCtx(() => [
          _createVNode(AnnotationSeveritySelect, {
            service: _unref(inspectionGroupData)?.service,
            modelValue: fields.severity,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fields.severity) = $event)),
            disabled: fields.type === null,
            isDisabled: fields.type === null
          }, null, 8, ["service", "modelValue", "disabled", "isDisabled"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(_unref(NkFormItem), {
        label: _ctx.$t('misc.comment'),
        prop: "comment"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkTextArea), {
            modelValue: fields.comment,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((fields.comment) = $event)),
            disabled: fields.type === null,
            isDisabled: fields.type === null
          }, null, 8, ["modelValue", "disabled", "isDisabled"])
        ]),
        _: 1
      }, 8, ["label"])
    ]),
    _: 1
  }, 8, ["fields"]))
}
}

})